/**
 * @fileoverview 配乗シミュレーション（SmartHR配置シミュレーション型）
 * @description 船を列にした顔写真カードのドラッグ&ドロップで配乗案を編集し、
 *              複数案を名前付きで保存（検討中/発令済/不採用）。列ごとに定員・平均年齢・
 *              船長不在・免状期限などの充足チェックを自動表示する。
 *              「発令」で効果日つきの乗下船予約へ変換（当日以前は即時反映）し、
 *              対象者へお知らせ配信できる。発令予約タブで予約の一覧・取消・単発発令も可能。
 */
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Ship, Anchor, Save, Trash2, X,
  RotateCw, Send, LayoutGrid, CalendarClock, CalendarDays, Search, Copy, FileDown, FileUp,
} from 'lucide-react';
import * as api from '../services/api';
import AvatarEditor from '../components/AvatarEditor';
import SearchableSelect from '../components/SearchableSelect';
import BoardingExcelImport from '../components/BoardingExcelImport';
import type { BoardingExcelImportHandle } from '../components/BoardingExcelImport';
import ExcelMenu from '../components/ExcelMenu';
import { hasFeature } from '../utils/featureFlags';

/** 配員基準の役職グループ（backend routes/boarding-sheet.ts の ROLE_GROUPS と同じ定義） */
const SIM_ROLE_GROUPS: Record<string, string[]> = {
  '船長': ['船長'],
  '航海士': ['一等航海士', '二等航海士', '三等航海士', '航海士', '一航士', '二航士', '三航士', '副船長'],
  '機関長': ['機関長'],
  '機関士': ['一等機関士', '二等機関士', '三等機関士', '機関士', '一機士', '二機士'],
  '甲板部員': ['甲板長', '甲板員'],
  '機関員': ['機関員'],
  '司厨': ['司厨長', '司厨員'],
};
function matchesRoleGroup(position: string | null | undefined, group: string): boolean {
  const toks = String(position ?? '').split('/').map(s => s.trim()).filter(Boolean);
  const list = SIM_ROLE_GROUPS[group] ?? [group];
  return toks.some(t => list.includes(t));
}
import TalentDetailDrawer, {
  Pill, fmtDate, calcAge, expiryInfo, daysUntil, setTalentThresholds,
} from '../components/TalentDetailDrawer';

// ── 型 ────────────────────────────────────────────────────────────────────
interface RosterRow {
  id: string;
  employee_code: string;
  user_name: string;
  name_kana: string | null;
  avatar_path: string | null;
  birth_date: string | null;
  license_grade: string | null;
  license_expiry_date: string | null;
  crew_note: string | null;
  /** 辞令上の役職（ユーザー管理で設定）。乗船中に執る職＝配乗の position とは別 */
  rank_position: string | null;
  current: { vessel_id: string; vessel_name: string; position: string | null; boarding_days?: number | null } | null;
  scheduled: { vessel_id: string; vessel_name: string; position: string | null; boarded_at: string | null } | null;
  days_ashore: number | null;
}
interface Vessel { id: string; vessel_name: string; vessel_type?: string | null; capacity?: number }
interface Plan {
  id: string; name: string; status: 'draft' | 'issued' | 'rejected';
  effective_date: string | null; issued_at: string | null;
  created_by_name: string | null; updated_at: string; item_count?: number;
}
interface Assignment { vessel_id: string | null; position: string | null }
interface Order {
  assignment_id: string; order_type: 'board' | 'disembark';
  user_id: string; user_name: string; avatar_path: string | null;
  vessel_id: string | null; vessel_name: string | null; position: string | null;
  effective_date: string;
}

const PLAN_STATUS: Record<string, { label: string; bg: string; fg: string }> = {
  draft:    { label: '検討中', bg: '#eff6ff', fg: '#2563eb' },
  issued:   { label: '発令済', bg: '#f0fdf4', fg: '#16a34a' },
  rejected: { label: '不採用', bg: '#f1f5f9', fg: '#94a3b8' },
};

const todayYmd = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);

// ── 乗組員カード ──────────────────────────────────────────────────────────
const CrewCard: React.FC<{
  row: RosterRow;
  assigned: Assignment;
  positions: string[];
  vessels: Vessel[];
  selected: boolean;
  onToggleSelect: (userId: string) => void;
  onDragStart: (userId: string) => void;
  onDragEnd: () => void;
  onPositionChange: (userId: string, position: string | null) => void;
  onMove: (userId: string, vesselId: string | null) => void;
  onOpenDetail: (userId: string) => void;
  leaveInfo?: { status: string } | null;
  /** 執職期間の登録（基準日時点）。役職と異なる職務に従事している場合のみ入る */
  dutyInfo?: { duty_position: string; end_date: string | null; note?: string | null } | null;
}> = ({ row, assigned, positions, vessels, selected, onToggleSelect, onDragStart, onDragEnd, onPositionChange, onMove, onOpenDetail, leaveInfo, dutyInfo }) => {
  const curVessel = row.current?.vessel_id ?? null;
  const moved = (assigned.vessel_id ?? null) !== curVessel;
  const licInfo = expiryInfo(row.license_expiry_date);
  const age = calcAge(row.birth_date);
  const rank = (row.rank_position ?? '').trim();
  // 役職は「船長/安全管理者」のように2つ併記できる（dual_positions）。順序違いを執職扱いしないよう
  // 集合として比較する（単一役職なら従来と同じ比較になる）。
  const posKey = (s: string) => s.split(/[/／]/).map(x => x.trim()).filter(Boolean).sort().join('/');
  // 執職 = 実際に執る職（執職期間の登録 > 乗船中の役職）が辞令上の役職と異なるとき
  const actingPos = (dutyInfo?.duty_position ?? assigned.position ?? '').trim();
  const acting = assigned.vessel_id !== null && !!actingPos && !!rank && posKey(actingPos) !== posKey(rank);
  // 役職2つ併記の編集用（主役職＋第2役職。官庁提出書類に両方印字するための登録）
  const dualOn = hasFeature('dual_positions');
  const posParts = (assigned.position ?? '').split(/[/／]/).map(x => x.trim()).filter(Boolean);
  const posMain = posParts[0] ?? '';
  const posSub = posParts[1] ?? '';
  const joinPos = (a: string, b: string): string | null => (a && b ? `${a}/${b}` : (a || b || null));
  // 変更内容バッジ
  let diffBadge: React.ReactNode = null;
  if (moved) {
    if (assigned.vessel_id === null) diffBadge = <Pill bg="#fef2f2" fg="#dc2626" border="#fecaca">下船</Pill>;
    else if (curVessel === null) diffBadge = <Pill bg="#f0fdf4" fg="#16a34a" border="#bbf7d0">新規乗船</Pill>;
    else diffBadge = <Pill bg="#fffbeb" fg="#b45309" border="#fde68a">移動</Pill>;
  }
  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('text/plain', row.id); e.dataTransfer.effectAllowed = 'move'; onDragStart(row.id); }}
      onDragEnd={onDragEnd}
      title={acting ? `執職中: 辞令は${rank}／この船では${actingPos}の職務に従事` : undefined}
      style={{
        background: selected ? '#f0f9ff' : acting ? '#fffdf4' : '#fff',
        border: `1.5px solid ${selected ? '#0ea5e9' : moved ? '#f59e0b' : acting ? '#f2dca6' : '#e5e7eb'}`, borderRadius: 10,
        borderLeft: acting ? '4px solid #f59e0b' : undefined,
        padding: '8px 10px', cursor: 'grab', userSelect: 'none',
        boxShadow: selected ? '0 2px 8px rgba(14,165,233,0.18)' : moved ? '0 2px 8px rgba(245,158,11,0.18)' : acting ? '0 2px 8px rgba(245,158,11,0.13)' : '0 1px 2px rgba(15,23,42,0.05)',
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={selected} onChange={() => onToggleSelect(row.id)}
          onClick={e => e.stopPropagation()} title="選択してまとめて移動"
          style={{ accentColor: '#0ea5e9', cursor: 'pointer', flexShrink: 0, margin: 0 }} />
        <div onClick={e => { e.stopPropagation(); onOpenDetail(row.id); }} style={{ cursor: 'pointer' }} title="詳細を表示">
          <AvatarEditor entityId={row.id} type="user" imagePath={row.avatar_path} displayName={row.user_name} size={34} readOnly onUpdated={() => {}} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); onOpenDetail(row.id); }}>
              {row.user_name}
            </span>
            {age != null && <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0 }}>{age}歳</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
            {/* 辞令上の役職（ユーザー管理で設定した会社としての立場） */}
            {rank
              ? <span title="辞令上の役職（ユーザー管理で設定）"
                  style={{ fontSize: 9.5, fontWeight: 800, color: '#334155', background: '#eef2f7', border: '1px solid #dbe3ec', borderRadius: 999, padding: '0 6px', whiteSpace: 'nowrap' }}>
                  {rank}
                </span>
              : <span title="ユーザー管理で辞令上の役職が未設定です" style={{ fontSize: 9.5, color: '#94a3b8' }}>役職未設定</span>}
            {acting && (
              <span title={`この船では${actingPos}の職務に従事（執職）${dutyInfo?.end_date ? `／${dutyInfo.end_date}まで` : ''}`}
                style={{ fontSize: 9.5, fontWeight: 800, color: '#92600a', background: '#fdf3e3', border: '1px solid #f2dca6', borderRadius: 999, padding: '0 6px', whiteSpace: 'nowrap' }}>
                執 {actingPos}
              </span>
            )}
            {row.license_grade
              ? <span style={{ fontSize: 9.5, color: '#a21caf', background: '#fdf4ff', border: '1px solid #f5d0fe', borderRadius: 999, padding: '0 6px', whiteSpace: 'nowrap' }}>
                  {row.license_grade}{(licInfo.level === 'expired' || licInfo.level === 'soon') && ' ⚠'}
                </span>
              : <span style={{ fontSize: 9.5, color: '#94a3b8' }}>免状なし</span>}
            {row.crew_note && <span title={row.crew_note} style={{ fontSize: 10, color: '#b45309' }}>⚠注意</span>}
            {leaveInfo && (
              leaveInfo.status === 'approved'
                ? <span title="承認済みの休暇" style={{ fontSize: 9.5, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '0 6px', whiteSpace: 'nowrap' }}>🌴休暇</span>
                : <span title="申請中の希望休" style={{ fontSize: 9.5, fontWeight: 700, color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '0 6px', whiteSpace: 'nowrap' }}>🌴希望休</span>
            )}
            {diffBadge}
          </div>
        </div>
        {/* ドラッグ不要の移動メニュー（船が多くて横断ドラッグが大変なとき用） */}
        <select
          value=""
          onChange={e => { const v = e.target.value; if (v) onMove(row.id, v === '_pool' ? null : v); }}
          onClick={e => e.stopPropagation()}
          title="クリックで移動先を選択（ドラッグ不要）"
          style={{
            width: 26, height: 24, flexShrink: 0, border: '1px solid #e2e8f0', borderRadius: 6,
            background: '#f8fafc', color: '#64748b', fontSize: 12, cursor: 'pointer',
            appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
            textAlign: 'center', paddingLeft: 6,
          }}>
          <option value="">⇄</option>
          {assigned.vessel_id !== null && <option value="_pool">⚓ 陸上（下船）へ</option>}
          {vessels.filter(v => v.id !== assigned.vessel_id).map(v => (
            <option key={v.id} value={v.id}>🚢 {v.vessel_name} へ</option>
          ))}
        </select>
      </div>
      {/* 乗船中の役職（＝この船で執る職）と「下船」。船に配置されている場合のみ表示。
          辞令上の役職はユーザー管理で管理し、ここでは「乗船中だけの職」を選ぶ。
          両者が異なるとカードが執職としてハイライトされる。
          下船は⇄メニューの中にもあるが気付きにくいので、1クリックで押せるボタンを出す。 */}
      {assigned.vessel_id !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: acting ? '#92600a' : '#94a3b8', flexShrink: 0 }}>乗船中</span>
          <select
            value={dualOn ? posMain : (assigned.position ?? '')}
            onChange={e => onPositionChange(row.id, dualOn ? joinPos(e.target.value, posSub) : (e.target.value || null))}
            onClick={e => e.stopPropagation()}
            title={dutyInfo ? `執職期間の登録により ${dutyInfo.duty_position} として従事中（乗下船表で編集）` : 'この船で執る職（乗船中のみ有効）'}
            style={{
              flex: 1, minWidth: 0, padding: '3px 6px', borderRadius: 6, fontSize: 11, cursor: 'pointer',
              border: `1px solid ${acting ? '#f2dca6' : '#e2e8f0'}`,
              background: acting ? '#fdf9f0' : '#f8fafc',
              color: acting ? '#92600a' : '#334155', fontWeight: acting ? 800 : 400,
            }}>
            <option value="">役職なし</option>
            {positions.map(p => <option key={p} value={p}>{p}</option>)}
            {dualOn
              ? (posMain && !positions.includes(posMain) && <option value={posMain}>{posMain}</option>)
              : (assigned.position && !positions.includes(assigned.position) && <option value={assigned.position}>{assigned.position}</option>)}
          </select>
          <button
            onClick={e => { e.stopPropagation(); onMove(row.id, null); }}
            title={`${row.user_name} を陸上へ移します（発令するまで実際の配乗は変わりません）`}
            style={{
              flexShrink: 0, padding: '3px 9px', border: '1px solid #fecaca', borderRadius: 6,
              background: '#fef2f2', color: '#dc2626', fontSize: 11, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
            ⚓ 下船
          </button>
        </div>
      )}
      {/* 第2役職（dual_positions の会社のみ）。「船長/安全管理者」のように併記して書類へ印字する */}
      {dualOn && assigned.vessel_id !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', flexShrink: 0 }}>＋役職</span>
          <select
            value={posSub}
            onChange={e => onPositionChange(row.id, joinPos(posMain, e.target.value))}
            onClick={e => e.stopPropagation()}
            title="第2役職（任意）。登録すると「船長/安全管理者」のように併記され、乗組員名簿などの書類にも両方印字されます"
            style={{ flex: 1, minWidth: 0, padding: '3px 6px', borderRadius: 6, fontSize: 11, cursor: 'pointer', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#334155' }}>
            <option value="">（なし）</option>
            {positions.filter(p => p !== posMain).map(p => <option key={p} value={p}>{p}</option>)}
            {posSub && !positions.includes(posSub) && <option value={posSub}>{posSub}</option>}
          </select>
        </div>
      )}

      {/* 陸上へ移した直後だけ、元の船に戻せるようにする（押し間違いの取り消し） */}
      {moved && assigned.vessel_id === null && curVessel !== null && (
        <button
          onClick={e => { e.stopPropagation(); onMove(row.id, curVessel); }}
          title="この下船を取り消して元の船に戻します"
          style={{
            width: '100%', marginTop: 6, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 6,
            background: '#f8fafc', color: '#475569', fontSize: 11, fontWeight: 700, cursor: 'pointer',
          }}>
          ↩ 下船を取り消す（{vessels.find(v => v.id === curVessel)?.vessel_name ?? '元の船'} へ戻す）
        </button>
      )}
    </div>
  );
};

// ── メインページ ──────────────────────────────────────────────────────────
const CrewSimPage: React.FC = () => {
  const [tab, setTab] = useState<'board' | 'orders' | 'situation'>('board');
  // 指定日の配乗状況（読み取り専用ビュー）
  const [sitDate, setSitDate] = useState(todayYmd());
  const [sitRows, setSitRows] = useState<Array<{ user_id: string; user_name: string; vessel_id: string | null; vessel_name: string | null; position: string | null; leave_status: string | null }>>([]);
  const [sitLoading, setSitLoading] = useState(false);
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [vessels, setVessels] = useState<Vessel[]>([]);
  const [positions, setPositions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  // 希望休/休暇の反映（基準日 boardDate 時点の休暇を user_id で引く。承認済み/申請中）
  const [leaveByUser, setLeaveByUser] = useState<Record<string, { status: string }>>({});
  // シミュレーションボードの基準日（この日の乗下船・休暇を状況として反映）
  const [boardDate, setBoardDate] = useState(todayYmd());
  const [asOfByUser, setAsOfByUser] = useState<Record<string, { vessel_id: string | null; position: string | null; leave_status: string | null }>>({});

  // 配乗案
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState<string | null>(null);
  const [planName, setPlanName] = useState('新しい配乗案');
  const [planStatus, setPlanStatus] = useState<'draft' | 'issued' | 'rejected'>('draft');
  const [assign, setAssign] = useState<Record<string, Assignment>>({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  // 発令ダイアログ
  const [issueOpen, setIssueOpen] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(todayYmd());
  const [notify, setNotify] = useState(true);
  const [issuing, setIssuing] = useState(false);

  // 発令予約タブ
  const [orders, setOrders] = useState<Order[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [newOrder, setNewOrder] = useState({ user_id: '', order_type: 'board', vessel_id: '', position: '', effective_date: todayYmd(), notify: true });
  const [orderSaving, setOrderSaving] = useState(false);

  const [poolQuery, setPoolQuery] = useState('');
  const [hoverCol, setHoverCol] = useState<string | null>(null);
  // Excelメニューから取込のファイル選択を開くためのハンドル
  const importRef = useRef<BoardingExcelImportHandle>(null);
  // 配員基準マスタ（vessel_id → rules）。乗下船表の設定から編集できる
  const [manningRules, setManningRules] = useState<Record<string, Array<{ role_name: string; min_count: number }>>>({});
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<{ data: { rules: Array<{ vessel_id: string; role_name: string; min_count: number }> } }>('/boarding-sheet/manning-rules');
        const map: Record<string, Array<{ role_name: string; min_count: number }>> = {};
        for (const rule of r.data?.rules ?? []) {
          (map[rule.vessel_id] ??= []).push({ role_name: rule.role_name, min_count: rule.min_count });
        }
        setManningRules(map);
      } catch { /* noop */ }
    })();
  }, []);
  const [detailUserId, setDetailUserId] = useState<string | null>(null);
  const dragUser = useRef<string | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);

  // ドラッグ中は全列をコンパクトなドロップ帯に切り替える（全船を画面内に収める）
  const [dragging, setDragging] = useState(false);
  // 表示する船の絞り込み（非表示でも移動メニュー・一括移動の宛先には出る）
  const [hiddenVessels, setHiddenVessels] = useState<Set<string>>(new Set());
  // 複数選択してまとめて移動
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDest, setBulkDest] = useState('');

  const toggleSelect = useCallback((userId: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  }, []);

  // ドラッグが画面外等で終了してもコンパクト表示が残らないようにする保険
  useEffect(() => {
    const reset = () => { setDragging(false); setHoverCol(null); };
    window.addEventListener('dragend', reset);
    window.addEventListener('drop', reset);
    return () => { window.removeEventListener('dragend', reset); window.removeEventListener('drop', reset); };
  }, []);

  // ── 種別・大きさ（定員規模）による表示グループ ──────────────────────
  const typeGroups = useMemo(() => {
    const map = new Map<string, Vessel[]>();
    vessels.forEach(v => {
      const t = v.vessel_type?.trim() || 'その他';
      const list = map.get(t) ?? [];
      list.push(v);
      map.set(t, list);
    });
    return [...map.entries()];
  }, [vessels]);

  const sizeBucket = (v: Vessel): string => {
    const c = v.capacity ?? 0;
    if (c >= 15) return '大型（定員15名〜）';
    if (c >= 10) return '中型（定員10〜14名）';
    return '小型（定員〜9名）';
  };
  const sizeGroups = useMemo(() => {
    const order = ['小型（定員〜9名）', '中型（定員10〜14名）', '大型（定員15名〜）'];
    const map = new Map<string, Vessel[]>();
    vessels.forEach(v => {
      const k = sizeBucket(v);
      const list = map.get(k) ?? [];
      list.push(v);
      map.set(k, list);
    });
    return order.filter(k => map.has(k)).map(k => [k, map.get(k)!] as [string, Vessel[]]);
  }, [vessels]);

  // グループ単位の表示トグル: 全部表示中なら隠す、1隻でも隠れていれば全部表示
  const toggleVesselGroup = (group: Vessel[]) => {
    setHiddenVessels(prev => {
      const next = new Set(prev);
      const allVisible = group.every(v => !next.has(v.id));
      if (allVisible) group.forEach(v => next.add(v.id));
      else group.forEach(v => next.delete(v.id));
      return next;
    });
  };

  // ドラッグ中に画面端へ近づいたらボードを自動で横スクロール（船が多い場合の横断ドラッグ対策）
  const autoScrollOnDrag = useCallback((e: React.DragEvent) => {
    const el = boardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const EDGE = 100, STEP = 30;
    if (e.clientX < r.left + EDGE) el.scrollLeft -= STEP;
    else if (e.clientX > r.right - EDGE) el.scrollLeft += STEP;
  }, []);

  // ── データ読込 ─────────────────────────────────────────────────────
  const buildAssignFromCurrent = useCallback((roster: RosterRow[]): Record<string, Assignment> => {
    const map: Record<string, Assignment> = {};
    for (const r of roster) map[r.id] = { vessel_id: r.current?.vessel_id ?? null, position: r.current?.position ?? null };
    return map;
  }, []);

  const loadRoster = useCallback(async () => {
    const res = await api.get<any>('/talent/roster');
    setTalentThresholds(res.thresholds);
    const roster = (res.data ?? []) as RosterRow[];
    setRows(roster);
    return roster;
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const res = await api.get<any>('/crew-plans');
      setPlans((res.data ?? []) as Plan[]);
    } catch (e) { console.error('[CrewSim/plans]', e); }
  }, []);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = await api.get<any>('/crew-plans/orders');
      setOrders((res.data ?? []) as Order[]);
    } catch (e) { console.error('[CrewSim/orders]', e); }
    setOrdersLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [roster, v] = await Promise.all([loadRoster(), api.get<any>('/vessels')]);
        setVessels(((v.vessels ?? v.data ?? []) as Vessel[]));
        setAssign(buildAssignFromCurrent(roster));
      } catch (e) { console.error('[CrewSim/init]', e); }
      try {
        const p = await api.get<any>('/position-masters');
        setPositions(((p.data ?? []) as Array<{ name: string }>).map(x => x.name));
      } catch { /* マスタ未整備なら空 */ }
      // 基準日の状況（乗下船・休暇）は下の boardDate 用 effect で取得する
      await loadPlans();
      setLoading(false);
    })();
  }, [loadRoster, loadPlans, buildAssignFromCurrent]);

  useEffect(() => { if (tab === 'orders') loadOrders(); }, [tab, loadOrders]);

  const loadSituation = useCallback(async (d: string) => {
    setSitLoading(true);
    try { const r = await api.get<any>('/crew-plans/as-of', { date: d }); setSitRows(r.data?.rows ?? []); }
    catch { setSitRows([]); }
    setSitLoading(false);
  }, []);
  useEffect(() => { if (tab === 'situation') loadSituation(sitDate); }, [tab, sitDate, loadSituation]);

  // 基準日にドック・入渠中の船（乗下船表のドックイベントと連動。配員警告を抑制しバッジ表示）
  const [dockByVessel, setDockByVessel] = useState<Record<string, { end_date: string }>>({});
  // 基準日に執職中の乗組員（乗下船表で登録した執職期間。カードのハイライト・配員の実効判定に使う）
  const [dutyByUser, setDutyByUser] = useState<Record<string, { duty_position: string; end_date: string | null; note?: string | null }>>({});

  // 基準日の乗下船・休暇を取得し、ボードのバッジ（🌴休暇/希望休）と状況サマリに反映
  // 基準日の状況を1本のAPIでまとめて取得する（乗下船・休暇・ドック・執職）。
  // 以前は3本に分けていたが、SPA初回ロードのバースト（アバター多数）と合わせて
  // nginxのレート制限(429)に当たり基準日サマリが空になることがあったため統合した。
  const loadAsOf = useCallback(async (d: string) => {
    try {
      const r = await api.get<any>('/crew-plans/as-of', { date: d });
      const rows = (r.data?.rows ?? []) as Array<{ user_id: string; vessel_id: string | null; position: string | null; leave_status: string | null }>;
      const ao: Record<string, { vessel_id: string | null; position: string | null; leave_status: string | null }> = {};
      const lv: Record<string, { status: string }> = {};
      for (const x of rows) {
        ao[x.user_id] = { vessel_id: x.vessel_id, position: x.position, leave_status: x.leave_status };
        if (x.leave_status) lv[x.user_id] = { status: x.leave_status };
      }
      setAsOfByUser(ao); setLeaveByUser(lv);

      const dk: Record<string, { end_date: string }> = {};
      for (const x of (r.data?.docks ?? []) as Array<{ vessel_id: string; end_date: string }>) dk[x.vessel_id] = { end_date: x.end_date };
      setDockByVessel(dk);

      const dt: Record<string, { duty_position: string; end_date: string | null; note?: string | null }> = {};
      for (const x of (r.data?.duties ?? []) as Array<{ user_id: string; duty_position: string; end_date: string | null; note: string | null }>) {
        dt[x.user_id] = { duty_position: x.duty_position, end_date: x.end_date, note: x.note };
      }
      setDutyByUser(dt);
    } catch { setAsOfByUser({}); setLeaveByUser({}); setDockByVessel({}); setDutyByUser({}); }
  }, []);
  useEffect(() => { if (!loading) loadAsOf(boardDate); }, [boardDate, loading, loadAsOf]);

  // 基準日の乗下船配置をボードに反映（現在編集中は確認）
  const applyAsOfToBoard = () => {
    if (dirty && !window.confirm('編集中の内容を破棄して、この日の配置をボードに反映しますか？')) return;
    const next: Record<string, Assignment> = {};
    for (const r of rows) {
      const a = asOfByUser[r.id];
      next[r.id] = { vessel_id: a?.vessel_id ?? null, position: a?.position ?? null };
    }
    setAssign(next); setPlanId(null); setPlanName(`${boardDate} の配置から`); setPlanStatus('draft'); setDirty(true);
  };

  // ── 配乗案の操作 ────────────────────────────────────────────────────
  const resetToCurrent = () => { setAssign(buildAssignFromCurrent(rows)); setDirty(false); };

  const selectPlan = async (id: string) => {
    if (!id) { setPlanId(null); setPlanName('新しい配乗案'); setPlanStatus('draft'); resetToCurrent(); return; }
    try {
      const res = await api.get<any>(`/crew-plans/${id}`);
      const p = res.data;
      setPlanId(p.id); setPlanName(p.name); setPlanStatus(p.status);
      // 案の内容を反映（案に無いユーザーは現状のまま）
      const base = buildAssignFromCurrent(rows);
      for (const it of (p.items ?? []) as Array<{ user_id: string; vessel_id: string | null; position: string | null }>) {
        if (base[it.user_id] !== undefined) base[it.user_id] = { vessel_id: it.vessel_id ?? null, position: it.position ?? null };
      }
      setAssign(base);
      setDirty(false);
    } catch { alert('配乗案の読み込みに失敗しました'); }
  };

  const itemsPayload = () => Object.entries(assign).map(([user_id, a]) => ({ user_id, vessel_id: a.vessel_id, position: a.position }));

  const savePlan = async (asNew = false): Promise<string | null> => {
    let name = planName.trim();
    if (asNew || !planId) {
      const input = window.prompt('配乗案の名前を入力してください', asNew ? `${name}（複製）` : name);
      if (!input?.trim()) return null;
      name = input.trim();
    }
    setSaving(true);
    try {
      if (planId && !asNew) {
        await api.put(`/crew-plans/${planId}`, { name, items: itemsPayload() });
        setPlanName(name); setDirty(false);
        await loadPlans();
        setSaving(false);
        return planId;
      }
      const res = await api.post<any>('/crew-plans', { name, items: itemsPayload() });
      const created = res.data;
      setPlanId(created.id); setPlanName(created.name); setPlanStatus('draft'); setDirty(false);
      await loadPlans();
      setSaving(false);
      return created.id as string;
    } catch (e: any) {
      alert(e?.message ?? '保存に失敗しました');
      setSaving(false);
      return null;
    }
  };

  const deletePlan = async () => {
    if (!planId) return;
    if (!window.confirm(`配乗案「${planName}」を削除しますか？`)) return;
    try {
      await api.del(`/crew-plans/${planId}`);
      setPlanId(null); setPlanName('新しい配乗案'); setPlanStatus('draft');
      resetToCurrent();
      await loadPlans();
    } catch { alert('削除に失敗しました'); }
  };

  const setPlanStatusRemote = async (status: 'draft' | 'rejected') => {
    if (!planId) { setPlanStatus(status); return; }
    try {
      await api.put(`/crew-plans/${planId}`, { status });
      setPlanStatus(status);
      await loadPlans();
    } catch { alert('状態の更新に失敗しました'); }
  };

  // ── ボード操作 ──────────────────────────────────────────────────────
  const move = (userId: string, vesselId: string | null) => {
    setAssign(prev => {
      const cur = prev[userId];
      if (!cur || cur.vessel_id === vesselId) return prev;
      // 船から陸上へ移す場合は役職を保持しない
      return { ...prev, [userId]: { vessel_id: vesselId, position: vesselId === null ? null : cur.position } };
    });
    setDirty(true);
  };
  const changePosition = (userId: string, position: string | null) => {
    setAssign(prev => ({ ...prev, [userId]: { ...prev[userId]!, position } }));
    setDirty(true);
  };

  // 複数人まとめて移動（選択カードのドラッグ／一括移動バーから）
  const moveMany = (userIds: string[], vesselId: string | null) => {
    setAssign(prev => {
      const next = { ...prev };
      for (const uid of userIds) {
        const cur = next[uid];
        if (!cur || cur.vessel_id === vesselId) continue;
        next[uid] = { vessel_id: vesselId, position: vesselId === null ? null : cur.position };
      }
      return next;
    });
    setDirty(true);
    setSelected(new Set());
  };

  const byId = useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);

  // 列ごとのメンバー（氏名順）
  const columnMembers = useMemo(() => {
    const map = new Map<string | null, RosterRow[]>();
    map.set(null, []);
    for (const v of vessels) map.set(v.id, []);
    for (const [uid, a] of Object.entries(assign)) {
      const r = byId.get(uid);
      if (!r) continue;
      const key = a.vessel_id !== null && map.has(a.vessel_id) ? a.vessel_id : (a.vessel_id === null ? null : a.vessel_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    for (const list of map.values()) list.sort((a, b) => a.user_name.localeCompare(b.user_name, 'ja'));
    return map;
  }, [assign, byId, vessels]);

  // 差分（現状 vs 案）
  const diffs = useMemo(() => {
    const out: Array<{ row: RosterRow; from: string | null; to: string | null; toName: string; position: string | null; kind: 'board' | 'disembark' | 'move' | 'position' }> = [];
    const vName = (id: string | null) => id === null ? null : (vessels.find(v => v.id === id)?.vessel_name ?? id);
    for (const [uid, a] of Object.entries(assign)) {
      const r = byId.get(uid);
      if (!r) continue;
      const cur = r.current?.vessel_id ?? null;
      const curPos = r.current?.position ?? null;
      if (cur !== (a.vessel_id ?? null)) {
        const kind = a.vessel_id === null ? 'disembark' : cur === null ? 'board' : 'move';
        out.push({ row: r, from: cur, to: a.vessel_id, toName: vName(a.vessel_id) ?? '陸上', position: a.position, kind });
      } else if (a.vessel_id !== null && (a.position ?? null) !== curPos) {
        out.push({ row: r, from: cur, to: a.vessel_id, toName: vName(a.vessel_id) ?? '', position: a.position, kind: 'position' });
      }
    }
    return out;
  }, [assign, byId, vessels]);

  // ── 発令 ────────────────────────────────────────────────────────────
  // 役職が空のまま船へ配置している乗組員（差分の有無に関わらず、案の中で船に居る全員が対象）。
  // 役職ベースの権限が効かなくなるため、1人でも居れば発令不可。サーバー側でも同じ条件で拒否する。
  const missingPosition = useMemo(() => {
    const out: Array<{ row: RosterRow; toName: string }> = [];
    for (const [uid, a] of Object.entries(assign)) {
      if (a.vessel_id === null || (a.position ?? '').trim()) continue;
      const r = byId.get(uid);
      if (!r) continue;
      out.push({ row: r, toName: vessels.find(v => v.id === a.vessel_id)?.vessel_name ?? '' });
    }
    return out;
  }, [assign, byId, vessels]);

  const issue = async () => {
    if (diffs.length === 0) { alert('現在の配乗との差分がありません'); return; }
    if (missingPosition.length > 0) {
      alert(`役職が未登録の乗組員がいます:\n${missingPosition.map(d => `・${d.row.user_name}（${d.toName}）`).join('\n')}\n\n船に配置する乗組員は役職の登録が必須です。カードの役職を選んでから発令してください。`);
      return;
    }
    setIssuing(true);
    try {
      // 未保存なら先に保存（発令は保存済みの案に対して行う）
      let id = planId;
      if (!id || dirty) {
        id = await savePlan(false);
        if (!id) { setIssuing(false); return; }
      }
      const res = await api.post<any>(`/crew-plans/${id}/issue`, { effective_date: effectiveDate, notify });
      const d = res.data ?? {};
      alert(`発令しました（乗船 ${d.boards ?? 0} / 下船 ${d.disembarks ?? 0} / 役職変更 ${d.position_changes ?? 0}${notify ? ` / 通知 ${d.notified ?? 0}件` : ''}）\n効果日: ${d.effective_date}`);
      setIssueOpen(false);
      setPlanStatus('issued');
      const roster = await loadRoster();
      setAssign(buildAssignFromCurrent(roster));
      setDirty(false);
      await loadPlans();
    } catch (e: any) {
      alert(e?.message ?? '発令に失敗しました');
    }
    setIssuing(false);
  };

  // ── 単発発令（発令予約タブ） ────────────────────────────────────────
  const submitOrder = async () => {
    if (!newOrder.user_id) { alert('対象の乗組員を選択してください'); return; }
    if (newOrder.order_type === 'board' && !newOrder.vessel_id) { alert('乗船先の船舶を選択してください'); return; }
    if (newOrder.order_type === 'board' && !newOrder.position) { alert('役職を選択してください（乗船発令には役職の登録が必須です）'); return; }
    setOrderSaving(true);
    try {
      await api.post('/crew-plans/orders', {
        user_id: newOrder.user_id,
        order_type: newOrder.order_type,
        vessel_id: newOrder.order_type === 'board' ? newOrder.vessel_id : undefined,
        position: newOrder.position || undefined,
        effective_date: newOrder.effective_date,
        notify: newOrder.notify,
      });
      setNewOrder(o => ({ ...o, user_id: '', vessel_id: '', position: '' }));
      await Promise.all([loadOrders(), loadRoster().then(r => { if (!dirty) setAssign(buildAssignFromCurrent(r)); })]);
    } catch (e: any) { alert(e?.message ?? '発令の登録に失敗しました'); }
    setOrderSaving(false);
  };

  const cancelOrder = async (o: Order) => {
    const label = o.order_type === 'board' ? '乗船予定' : '下船予定';
    if (!window.confirm(`${o.user_name} の${label}（${fmtDate(o.effective_date)}）を取り消しますか？`)) return;
    try {
      await api.del(`/crew-plans/orders/${o.order_type === 'board' ? 'scheduled' : 'departure'}/${o.assignment_id}`);
      await Promise.all([loadOrders(), loadRoster().then(r => { if (!dirty) setAssign(buildAssignFromCurrent(r)); })]);
    } catch { alert('取消に失敗しました'); }
  };

  // ── 列レンダリング ──────────────────────────────────────────────────
  const renderColumn = (vessel: Vessel | null) => {
    const key = vessel?.id ?? null;
    const members = columnMembers.get(key) ?? [];
    const shown = key === null && poolQuery.trim()
      ? members.filter(m => m.user_name.toLowerCase().includes(poolQuery.trim().toLowerCase()) || (m.name_kana ?? '').toLowerCase().includes(poolQuery.trim().toLowerCase()))
      : members;
    const capacity = vessel?.capacity ?? 0;
    const overCap = vessel !== null && capacity > 0 && members.length > capacity;
    // 配員基準（マスタ）による不足チェック。基準が無い船は従来の船長不在チェックのみ
    // ドック・入渠中の船は運航していないため不足警告を出さない（🔧バッジで状態を示す）
    const docked = key ? dockByVessel[key] : undefined;
    const vesselRules = key ? (manningRules[key] ?? []) : [];
    // 実効の職務 = 執職期間の登録 > 乗船中の役職（船長休暇中に一等航海士が執職・船長なら基準を満たす）
    const effPos = (uid: string) => dutyByUser[uid]?.duty_position ?? assign[uid]?.position ?? '';
    const shortages = vessel !== null && members.length > 0 && !docked
      ? vesselRules
          .map(r => ({ ...r, count: members.filter(m => matchesRoleGroup(effPos(m.id), r.role_name)).length }))
          .filter(r => r.count < r.min_count)
      : [];
    const noCaptain = vessel !== null && members.length > 0 && vesselRules.length === 0 && !docked
      // 「船長/安全管理者」のような併記でも船長として数える
      && !members.some(m => effPos(m.id).split(/[/／]/).some(t => t.trim() === '船長'));
    const licWarn = vessel !== null ? members.filter(m => ['expired', 'soon'].includes(expiryInfo(m.license_expiry_date).level)).length : 0;
    const ages = members.map(m => calcAge(m.birth_date)).filter((a): a is number => a != null);
    const avgAge = ages.length ? Math.round(ages.reduce((s, a) => s + a, 0) / ages.length) : null;
    const isHover = hoverCol === (key ?? '_pool');

    const compact = dragging; // ドラッグ中は全列を細いドロップ帯にして全船を画面内に収める
    const colWidth = compact ? 148 : key === null ? 280 : 260;

    return (
      <div key={key ?? '_pool'}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
        onDragEnter={() => setHoverCol(key ?? '_pool')}
        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setHoverCol(h => (h === (key ?? '_pool') ? null : h)); }}
        onDrop={e => {
          e.preventDefault();
          const uid = e.dataTransfer.getData('text/plain') || dragUser.current;
          if (uid) {
            // 選択中のカードをドラッグした場合は選択メンバー全員をまとめて移動
            if (selected.has(uid) && selected.size > 1) moveMany([...selected], key);
            else move(uid, key);
          }
          setHoverCol(null); dragUser.current = null; setDragging(false);
        }}
        style={{
          minWidth: colWidth, maxWidth: colWidth, flexShrink: 0,
          background: isHover ? '#e0f2fe' : '#f8fafc',
          border: `2px ${isHover ? 'dashed #0ea5e9' : 'solid #e2e8f0'}`,
          borderRadius: 12, display: 'flex', flexDirection: 'column',
          transition: 'background .1s, border-color .1s, min-width .15s, max-width .15s',
        }}>
        {/* 列ヘッダー */}
        <div style={{ padding: compact ? '8px 10px 6px' : '10px 12px 8px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            {vessel ? <Ship size={15} color="#0369a1" /> : <Anchor size={15} color="#64748b" />}
            <span style={{ fontSize: compact ? 12 : 13.5, fontWeight: 800, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {vessel?.vessel_name ?? '陸上（未配乗）'}
            </span>
            <span style={{
              fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: '1px 9px',
              background: overCap ? '#fef2f2' : '#eef2f7', color: overCap ? '#dc2626' : '#475569',
            }}>
              {members.length}{vessel && capacity > 0 ? `/${capacity}` : ''}名
            </span>
          </div>
          {/* 充足チェック（コンパクト時は省略） */}
          {!compact && (
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6, minHeight: 18 }}>
              {vessel && (
                <span style={{ fontSize: 10, fontWeight: 700, color: '#4338ca', background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 999, padding: '0 7px' }}>
                  {vessel.vessel_type?.trim() || 'その他'}
                </span>
              )}
              {avgAge != null && <span style={{ fontSize: 10, color: '#64748b' }}>平均{avgAge}歳</span>}
              {docked && (
                <Pill bg="#fdf3e3" fg="#b45309" border="#f2dca6">
                  🔧ドック中（〜{Number(docked.end_date.slice(5, 7))}/{Number(docked.end_date.slice(8, 10))}）
                </Pill>
              )}
              {overCap && <Pill bg="#fef2f2" fg="#dc2626" border="#fecaca">定員超過</Pill>}
              {noCaptain && <Pill bg="#fffbeb" fg="#b45309" border="#fde68a">船長不在</Pill>}
              {shortages.map(s => (
                <Pill key={s.role_name} bg="#fef2f2" fg="#b42318" border="#f4c7c3">{s.role_name}不足 {s.count}/{s.min_count}</Pill>
              ))}
              {licWarn > 0 && <Pill bg="#fff7ed" fg="#c2410c" border="#fed7aa">免状注意 {licWarn}名</Pill>}
            </div>
          )}
          {key === null && !compact && (
            <div style={{ position: 'relative', marginTop: 6 }}>
              <Search size={12} color="#94a3b8" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }} />
              <input value={poolQuery} onChange={e => setPoolQuery(e.target.value)} placeholder="陸上メンバーを検索"
                style={{ width: '100%', padding: '5px 8px 5px 26px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 11.5, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
            </div>
          )}
        </div>
        {/* コンパクト時: 大きなドロップ帯（カードはDOMに残したまま畳む＝ドラッグを中断させない） */}
        {compact && (
          <div style={{
            flex: 1, minHeight: 150, margin: 8, borderRadius: 10,
            border: `2px dashed ${isHover ? '#0ea5e9' : '#cbd5e1'}`,
            background: isHover ? '#e0f2fe' : 'transparent',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
            color: isHover ? '#0369a1' : '#94a3b8', fontSize: 12, fontWeight: 800, pointerEvents: 'none',
          }}>
            <span style={{ fontSize: 20 }}>{vessel ? '🚢' : '⚓'}</span>
            ここにドロップ
          </div>
        )}
        {/* カード */}
        <div style={compact
          ? { maxHeight: 0, overflow: 'hidden', padding: 0, display: 'flex', flexDirection: 'column', gap: 7 }
          : { padding: 10, display: 'flex', flexDirection: 'column', gap: 7, overflowY: 'auto', flex: 1, minHeight: 120 }}>
          {shown.map(m => (
            <CrewCard key={m.id} row={m} assigned={assign[m.id]!} positions={positions} vessels={vessels}
              selected={selected.has(m.id)}
              onToggleSelect={toggleSelect}
              onDragStart={uid => { dragUser.current = uid; setTimeout(() => setDragging(true), 0); }}
              onDragEnd={() => { setDragging(false); setHoverCol(null); dragUser.current = null; }}
              onPositionChange={changePosition}
              onMove={move}
              onOpenDetail={setDetailUserId}
              leaveInfo={leaveByUser[m.id] ?? null}
              dutyInfo={dutyByUser[m.id] ?? null} />
          ))}
          {shown.length === 0 && (
            <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 11.5, padding: '18px 0', border: '1.5px dashed #e2e8f0', borderRadius: 8 }}>
              ここにドラッグして{vessel ? '乗船' : '下船'}
            </div>
          )}
        </div>
      </div>
    );
  };

  const ORDER_KIND = {
    board:     { label: '乗船', bg: '#f0fdf4', fg: '#16a34a', border: '#bbf7d0' },
    disembark: { label: '下船', bg: '#fef2f2', fg: '#dc2626', border: '#fecaca' },
  } as const;

  return (
    <div className="page-wrap" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', boxSizing: 'border-box' }}>
      {/* ヘッダー */}
      <div className="top-bar" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">配乗シミュレーション</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            顔写真カードをドラッグ&ドロップで配置検討 → 発令で予約反映（効果日到来で自動乗下船）
          </p>
        </div>
      </div>

      {/* この画面の役割と反映タイミング（配乗シミュレーション／乗下船表／乗下船記録の使い分け） */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#1e3a8a' }}>
        <span><strong>この画面:</strong> 配乗案を作って「発令」すると、効果日に乗下船・役職が本番へ反映されます。役職の登録・変更もここ（発令で反映）。</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 12 }}><Link to="/crew-assign" style={{ fontWeight: 800, color: '#1d4ed8', textDecoration: 'none' }}>乗下船表 →</Link><Link to="/boarding" style={{ fontWeight: 800, color: '#1d4ed8', textDecoration: 'none' }}>乗下船記録 →</Link></span>
      </div>
      {/* タブ */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1.5px solid #e2e8f0', marginBottom: 14, flexShrink: 0, alignItems: 'center' }}>
        {([
          ['board', <LayoutGrid key="i" size={15} />, 'シミュレーションボード'],
          ['situation', <CalendarDays key="i" size={15} />, '指定日の状況'],
          ['orders', <CalendarClock key="i" size={15} />, `発令予約${orders.length > 0 ? `（${orders.length}）` : ''}`],
        ] as ['board' | 'orders' | 'situation', React.ReactNode, string][]).map(([key, icon, label]) => (
          <button key={key} onClick={() => setTab(key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '10px 20px', fontSize: 13.5, fontWeight: 700, cursor: 'pointer',
              border: 'none', background: 'transparent',
              color: tab === key ? '#0369a1' : '#64748b',
              borderBottom: tab === key ? '2.5px solid #0369a1' : '2.5px solid transparent',
              marginBottom: -1.5,
            }}>
            {icon}{label}
          </button>
        ))}
        {/* Excel関連（取込・フォーマットDL）を1つのメニューに集約。取込は乗下船表にも反映される */}
        <div style={{ marginLeft: 'auto', paddingBottom: 6 }}>
          <ExcelMenu items={[
            { icon: <FileUp size={15} />, label: 'Excelから取り込む', desc: 'イベント表を読み込んで発令予約を一括登録（乗下船表にも反映）', onClick: () => importRef.current?.openPicker() },
            {
              icon: <FileDown size={15} />, label: '取込用フォーマットを入手', desc: '記入例・乗組員一覧つきの空Excelをダウンロード',
              onClick: async () => {
                try {
                  const vid = vessels[0]?.id ?? '';
                  const res = await fetch(`${api.getApiBase()}/boarding-sheet/template?vessel_id=${vid}`, { headers: { Authorization: `Bearer ${api.getToken()}` } });
                  if (!res.ok) throw new Error('ダウンロードに失敗しました');
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `乗下船表_取込フォーマット_${vessels[0]?.vessel_name ?? '船'}.xlsx`; a.click();
                  URL.revokeObjectURL(url);
                } catch (e: any) { alert(e?.message ?? 'ダウンロードに失敗しました'); }
              },
            },
          ]} />
          <BoardingExcelImport
            ref={importRef} hideTrigger
            vessels={vessels.map(v => ({ id: v.id, vessel_name: v.vessel_name }))}
            users={rows.map(r => ({ id: r.id, user_name: r.user_name }))}
            onImported={loadOrders}
          />
        </div>
      </div>

      {/* ═══ ボードタブ ═══ */}
      {tab === 'board' && (
        <>
          {/* 案ツールバー */}
          <div className="card" style={{ padding: '12px 16px', flexShrink: 0, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <select value={planId ?? ''} onChange={e => selectPlan(e.target.value)}
                style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff', cursor: 'pointer', maxWidth: 240 }}>
                <option value="">＋ 新規（現在の配乗から）</option>
                {plans.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}（{PLAN_STATUS[p.status]?.label ?? p.status}）
                  </option>
                ))}
              </select>
              <input value={planName} onChange={e => { setPlanName(e.target.value); setDirty(true); }}
                style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 200 }}
                placeholder="案の名前" />
              {(() => { const st = PLAN_STATUS[planStatus] ?? PLAN_STATUS['draft']!; return <Pill bg={st.bg} fg={st.fg}>{st.label}</Pill>; })()}
              {dirty && <span style={{ fontSize: 11.5, color: '#b45309', fontWeight: 700 }}>● 未保存の変更</span>}

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn-o" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, padding: '7px 12px' }}
                  onClick={resetToCurrent} title="ボードを現在の配乗に戻す">
                  <RotateCw size={13} />現状に戻す
                </button>
                <button className="btn-o" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, padding: '7px 12px' }}
                  disabled={saving} onClick={() => savePlan(false)}>
                  <Save size={13} />{saving ? '保存中…' : '保存'}
                </button>
                <button className="btn-o" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, padding: '7px 12px' }}
                  disabled={saving} onClick={() => savePlan(true)} title="別名で保存">
                  <Copy size={13} />複製
                </button>
                {planId && (
                  <>
                    <button className="btn-o" style={{ fontSize: 12.5, padding: '7px 12px', color: planStatus === 'rejected' ? '#2563eb' : '#94a3b8' }}
                      onClick={() => setPlanStatusRemote(planStatus === 'rejected' ? 'draft' : 'rejected')}>
                      {planStatus === 'rejected' ? '検討中に戻す' : '不採用にする'}
                    </button>
                    <button className="btn-o" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, padding: '7px 12px', color: '#dc2626', borderColor: '#fecaca' }}
                      onClick={deletePlan}>
                      <Trash2 size={13} />削除
                    </button>
                  </>
                )}
                <button className="btn-p" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 18px' }}
                  disabled={diffs.length === 0}
                  onClick={() => { setEffectiveDate(todayYmd()); setIssueOpen(true); }}
                  title={diffs.length === 0 ? '現在の配乗との差分がありません' : ''}>
                  <Send size={14} />発令する{diffs.length > 0 ? `（${diffs.length}件）` : ''}
                </button>
              </div>
            </div>
          </div>

          {/* 基準日バー: この日の乗下船・休暇を反映（陸上・休暇の人数も表示） */}
          {!loading && (() => {
            const vals = Object.values(asOfByUser);
            const aboard = vals.filter(a => a.vessel_id && !a.leave_status).length;
            const leaveN = vals.filter(a => a.leave_status).length;
            const ashore = vals.filter(a => !a.vessel_id && !a.leave_status).length;
            const shift = (n: number | 'today') => {
              if (n === 'today') { setBoardDate(todayYmd()); return; }
              const b = new Date(boardDate + 'T00:00:00Z'); b.setUTCDate(b.getUTCDate() + n); setBoardDate(b.toISOString().slice(0, 10));
            };
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10, flexShrink: 0, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 12px' }}>
                <CalendarDays size={15} color="#0369a1" />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a' }}>基準日</span>
                <input type="date" value={boardDate} onChange={e => setBoardDate(e.target.value)}
                  style={{ padding: '6px 9px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 12.5, background: '#fff' }} />
                <div style={{ display: 'flex', gap: 3 }}>
                  {([[-7, '−1週'], [-1, '前日'], ['today', '今日'], [1, '翌日'], [7, '+1週']] as const).map(([k, lbl]) => (
                    <button key={String(k)} onClick={() => shift(k as number | 'today')} className="btn-o" style={{ fontSize: 11, padding: '4px 9px' }}>{lbl}</button>
                  ))}
                </div>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700, marginLeft: 4 }}>
                  この日: 乗船 <b style={{ color: '#0369a1' }}>{aboard}</b> ／ <span style={{ color: '#16a34a' }}>🌴休暇 <b>{leaveN}</b></span> ／ <span style={{ color: '#64748b' }}>⚓陸上 <b>{ashore}</b></span>
                </span>
                <button className="btn-o" onClick={applyAsOfToBoard} title="この日の乗下船状況をボードの配置に反映します"
                  style={{ marginLeft: 'auto', fontSize: 12, padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <RotateCw size={12} />この日の配置を反映
                </button>
              </div>
            );
          })()}

          {/* 表示する船の絞り込み（船が多い時は高さを抑えて内部スクロール＝ボードの縦領域を確保） */}
          {!loading && vessels.length > 3 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10, flexShrink: 0, maxHeight: 104, overflowY: 'auto' }}>
              <span style={{ fontSize: 11.5, fontWeight: 800, color: '#64748b' }}>表示する船:</span>
              <button
                onClick={() => setHiddenVessels(prev => prev.size === 0 ? new Set(vessels.map(v => v.id)) : new Set())}
                title={hiddenVessels.size === 0 ? 'クリックで全船を非表示（見たい船だけ選び直せます）' : 'クリックで全船を表示'}
                style={{
                  fontSize: 11.5, fontWeight: 700, padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
                  border: `1.5px solid ${hiddenVessels.size === 0 ? '#0369a1' : '#e2e8f0'}`,
                  background: hiddenVessels.size === 0 ? '#e0f2fe' : '#fff',
                  color: hiddenVessels.size === 0 ? '#0369a1' : '#64748b',
                }}>
                全船（{vessels.length}）
              </button>
              {vessels.map(v => {
                const hidden = hiddenVessels.has(v.id);
                const cnt = (columnMembers.get(v.id) ?? []).length;
                return (
                  <button key={v.id}
                    onClick={() => setHiddenVessels(prev => { const next = new Set(prev); if (next.has(v.id)) next.delete(v.id); else next.add(v.id); return next; })}
                    title={hidden ? 'クリックで表示' : 'クリックで非表示'}
                    style={{
                      fontSize: 11.5, fontWeight: 700, padding: '4px 12px', borderRadius: 999, cursor: 'pointer',
                      border: `1.5px solid ${hidden ? '#e2e8f0' : '#bae6fd'}`,
                      background: hidden ? '#f8fafc' : '#f0f9ff',
                      color: hidden ? '#cbd5e1' : '#0369a1',
                      textDecoration: hidden ? 'line-through' : 'none',
                    }}>
                    {v.vessel_name}（{cnt}）
                  </button>
                );
              })}
              {hiddenVessels.size > 0 && (
                <span style={{ fontSize: 11, color: '#94a3b8' }}>
                  ※ 非表示の船（{hiddenVessels.size}隻）へも「⇄」メニューや一括移動からは移動できます
                </span>
              )}
              {/* 種別・大きさ（定員規模）でまとめて切替 */}
              <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                <span style={{ fontSize: 11.5, fontWeight: 800, color: '#64748b' }}>種別・大きさで切替:</span>
                {typeGroups.map(([type, group]) => {
                  const visibleCnt = group.filter(v => !hiddenVessels.has(v.id)).length;
                  const allVisible = visibleCnt === group.length;
                  const noneVisible = visibleCnt === 0;
                  return (
                    <button key={`t-${type}`} onClick={() => toggleVesselGroup(group)}
                      title={allVisible ? `${type}の船をすべて非表示` : `${type}の船をすべて表示`}
                      style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                        border: `1.5px solid ${noneVisible ? '#e2e8f0' : '#c7d2fe'}`,
                        background: noneVisible ? '#f8fafc' : allVisible ? '#eef2ff' : '#fff',
                        color: noneVisible ? '#cbd5e1' : '#4338ca',
                        textDecoration: noneVisible ? 'line-through' : 'none',
                      }}>
                      {type}（{allVisible ? group.length : `${visibleCnt}/${group.length}`}）
                    </button>
                  );
                })}
                <span style={{ width: 1, height: 16, background: '#e2e8f0' }} />
                {sizeGroups.map(([label, group]) => {
                  const visibleCnt = group.filter(v => !hiddenVessels.has(v.id)).length;
                  const allVisible = visibleCnt === group.length;
                  const noneVisible = visibleCnt === 0;
                  return (
                    <button key={`s-${label}`} onClick={() => toggleVesselGroup(group)}
                      title={`${group.map(v => v.vessel_name).join('、')}`}
                      style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                        border: `1.5px solid ${noneVisible ? '#e2e8f0' : '#bbf7d0'}`,
                        background: noneVisible ? '#f8fafc' : allVisible ? '#f0fdf4' : '#fff',
                        color: noneVisible ? '#cbd5e1' : '#15803d',
                        textDecoration: noneVisible ? 'line-through' : 'none',
                      }}>
                      {label}（{allVisible ? group.length : `${visibleCnt}/${group.length}`}）
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* 一括移動バー（カードのチェックボックスで選択したメンバー） */}
          {selected.size > 0 && (
            <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 10, padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10, flexShrink: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: '#1d4ed8' }}>☑ {selected.size}名を選択中</span>
              <span style={{ fontSize: 11.5, color: '#64748b' }}>
                {[...selected].slice(0, 5).map(id => byId.get(id)?.user_name ?? '').join('、')}{selected.size > 5 ? ` 他${selected.size - 5}名` : ''}
              </span>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <select value={bulkDest} onChange={e => setBulkDest(e.target.value)}
                  style={{ padding: '6px 10px', border: '1.5px solid #bfdbfe', borderRadius: 8, fontSize: 12.5, background: '#fff', cursor: 'pointer' }}>
                  <option value="">移動先を選択…</option>
                  <option value="_pool">⚓ 陸上（下船）</option>
                  {vessels.map(v => <option key={v.id} value={v.id}>🚢 {v.vessel_name}</option>)}
                </select>
                <button className="btn-p" disabled={!bulkDest} style={{ fontSize: 12.5, padding: '7px 16px' }}
                  onClick={() => { if (!bulkDest) return; moveMany([...selected], bulkDest === '_pool' ? null : bulkDest); setBulkDest(''); }}>
                  まとめて移動
                </button>
                <button className="btn-o" style={{ fontSize: 12.5, padding: '7px 12px' }} onClick={() => setSelected(new Set())}>
                  選択解除
                </button>
              </div>
            </div>
          )}

          {/* ボード本体 */}
          {loading ? (
            <div className="card"><div className="empty" style={{ padding: 40 }}>読み込み中…</div></div>
          ) : (
            <div ref={boardRef} onDragOver={autoScrollOnDrag}
              style={{ display: 'flex', gap: 12, overflowX: 'auto', flexShrink: 0, height: 'clamp(460px, calc(100vh - 300px), 900px)', paddingBottom: 8, alignItems: 'stretch' }}>
              {renderColumn(null)}
              {vessels.filter(v => !hiddenVessels.has(v.id)).map(v => renderColumn(v))}
            </div>
          )}
        </>
      )}

      {/* ═══ 発令予約タブ ═══ */}
      {/* ═══ 指定日の状況タブ（読み取り専用） ═══ */}
      {tab === 'situation' && (() => {
        const shiftDate = (kind: string) => {
          if (kind === 'today') { setSitDate(todayYmd()); return; }
          const base = new Date(sitDate + 'T00:00:00Z');
          base.setUTCDate(base.getUTCDate() + Number(kind));
          setSitDate(base.toISOString().slice(0, 10));
        };
        const onLeave = sitRows.filter(r => r.leave_status);
        const ashore = sitRows.filter(r => !r.vessel_id && !r.leave_status);
        const aboard = sitRows.filter(r => r.vessel_id && !r.leave_status);
        const vesselCols = vessels
          .map(v => ({ v, members: aboard.filter(r => r.vessel_id === v.id) }))
          .filter(x => x.members.length > 0)
          .sort((a, b) => a.v.vessel_name.localeCompare(b.v.vessel_name, 'ja'));

        const sitCard = (r: typeof sitRows[number]) => (
          <div key={r.user_id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '6px 9px' }}>
            <AvatarEditor entityId={r.user_id} type="user" imagePath={byId.get(r.user_id)?.avatar_path ?? null} displayName={r.user_name} size={28} readOnly onUpdated={() => {}} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.user_name}</div>
              <div style={{ display: 'flex', gap: 4, marginTop: 1, flexWrap: 'wrap' }}>
                {r.position && <span style={{ fontSize: 9.5, color: '#334155', background: '#eef2f7', borderRadius: 999, padding: '0 6px' }}>{r.position}</span>}
                {r.leave_status === 'approved' && <span style={{ fontSize: 9.5, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 999, padding: '0 6px' }}>🌴休暇</span>}
                {r.leave_status === 'pending' && <span style={{ fontSize: 9.5, fontWeight: 700, color: '#d97706', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 999, padding: '0 6px' }}>🌴希望休(申請中)</span>}
              </div>
            </div>
          </div>
        );

        const column = (title: string, icon: React.ReactNode, tint: string, members: typeof sitRows) => (
          <div key={title} style={{ minWidth: 244, maxWidth: 244, flexShrink: 0, background: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: 12, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 7 }}>
              {icon}
              <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</span>
              <span style={{ fontSize: 11.5, fontWeight: 800, borderRadius: 999, padding: '1px 9px', background: tint, color: '#475569' }}>{members.length}名</span>
            </div>
            <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 7, overflowY: 'auto', flex: 1, minHeight: 120 }}>
              {members.length === 0
                ? <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: 11.5, padding: '18px 0' }}>該当なし</div>
                : members.map(sitCard)}
            </div>
          </div>
        );

        return (
          <>
            <div className="card" style={{ padding: '12px 16px', flexShrink: 0, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a' }}>📅 指定日の配乗状況</span>
              <input type="date" value={sitDate} onChange={e => setSitDate(e.target.value)}
                style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }} />
              <div style={{ display: 'flex', gap: 4 }}>
                {([['-7', '−1週'], ['-1', '前日'], ['today', '今日'], ['+1', '翌日'], ['+7', '+1週']] as const).map(([k, lbl]) => (
                  <button key={k} onClick={() => shiftDate(k)} className="btn-o" style={{ fontSize: 11.5, padding: '5px 10px' }}>{lbl}</button>
                ))}
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                乗船 <b style={{ color: '#0369a1' }}>{aboard.length}</b> ／ 休暇 <b style={{ color: '#16a34a' }}>{onLeave.length}</b> ／ 陸上 <b style={{ color: '#64748b' }}>{ashore.length}</b> 名
              </span>
            </div>
            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 14px', marginBottom: 10, fontSize: 12, color: '#1e3a8a', flexShrink: 0 }}>
              その日の乗下船記録・休暇から自動で状況を表示します（読み取り専用）。実際の配置変更は「シミュレーションボード」で行ってください。
            </div>
            {sitLoading ? (
              <div className="card"><div className="empty" style={{ padding: 40 }}>読み込み中…</div></div>
            ) : (
              <div style={{ display: 'flex', gap: 12, overflowX: 'auto', flexShrink: 0, height: 'clamp(460px, calc(100vh - 320px), 900px)', paddingBottom: 8, alignItems: 'stretch' }}>
                {onLeave.length > 0 && column('🌴 休暇・希望休', <CalendarDays size={15} color="#16a34a" />, '#dcfce7', onLeave)}
                {column('⚓ 陸上（下船中）', <Anchor size={15} color="#64748b" />, '#eef2f7', ashore)}
                {vesselCols.map(({ v, members }) => (
                  <div key={v.id}>{column(v.vessel_name, <Ship size={15} color="#0369a1" />, '#e0f2fe', members)}</div>
                ))}
                {vesselCols.length === 0 && aboard.length === 0 && (
                  <div className="empty" style={{ padding: 40 }}>この日に乗船している乗組員はいません</div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {tab === 'orders' && (
        <div style={{ overflowY: 'auto' }}>
          {/* 単発発令フォーム */}
          <div className="card" style={{ padding: '14px 18px' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#0f172a', marginBottom: 10 }}>新規発令（単発）</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ minWidth: 220 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>対象乗組員</div>
                <SearchableSelect value={newOrder.user_id} onChange={v => setNewOrder(o => ({ ...o, user_id: v }))}
                  options={rows.map(r => ({ value: r.id, label: r.user_name, sub: r.current?.vessel_name ?? '陸上', group: r.current?.vessel_name ?? '陸上（未配乗）' }))} />
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>区分</div>
                <select value={newOrder.order_type} onChange={e => setNewOrder(o => ({ ...o, order_type: e.target.value }))}
                  style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                  <option value="board">乗船</option>
                  <option value="disembark">下船</option>
                </select>
              </div>
              {newOrder.order_type === 'board' && (
                <>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>乗船先</div>
                    <select value={newOrder.vessel_id} onChange={e => setNewOrder(o => ({ ...o, vessel_id: e.target.value }))}
                      style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                      <option value="">選択してください</option>
                      {vessels.map(v => <option key={v.id} value={v.id}>{v.vessel_name}</option>)}
                    </select>
                  </div>
                  {(() => {
                    // 役職2つ併記（dual_positions）: "主役職/第2役職" を2つのselectで編集する
                    const dualOn = hasFeature('dual_positions');
                    const parts = (newOrder.position ?? '').split(/[/／]/).map(s => s.trim()).filter(Boolean);
                    const main = parts[0] ?? '';
                    const sub = parts[1] ?? '';
                    const join = (a: string, b: string) => (a && b ? `${a}/${b}` : (a || b));
                    return (
                      <>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>役職（必須）</div>
                          <select value={dualOn ? main : newOrder.position}
                            onChange={e => setNewOrder(o => ({ ...o, position: dualOn ? join(e.target.value, sub) : e.target.value }))}
                            style={{ padding: '8px 10px', border: `1.5px solid ${newOrder.position ? '#e2e8f0' : '#fca5a5'}`, borderRadius: 8, fontSize: 13, background: '#fff' }}>
                            <option value="">選択してください</option>
                            {positions.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>
                        </div>
                        {dualOn && (
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>第2役職（任意）</div>
                            <select value={sub}
                              onChange={e => setNewOrder(o => ({ ...o, position: join(main, e.target.value) }))}
                              title="登録すると「船長/安全管理者」のように併記され、書類にも両方印字されます"
                              style={{ padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                              <option value="">（なし）</option>
                              {positions.filter(p => p !== main).map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              )}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 3 }}>効果日</div>
                <input type="date" value={newOrder.effective_date} min={todayYmd()}
                  onChange={e => setNewOrder(o => ({ ...o, effective_date: e.target.value }))}
                  style={{ padding: '7px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#374151', cursor: 'pointer', paddingBottom: 8 }}>
                <input type="checkbox" checked={newOrder.notify} onChange={e => setNewOrder(o => ({ ...o, notify: e.target.checked }))}
                  style={{ accentColor: '#0066CC' }} />
                本人へお知らせ配信
              </label>
              <button className="btn-p" style={{ padding: '9px 18px', fontSize: 13 }} disabled={orderSaving} onClick={submitOrder}>
                {orderSaving ? '登録中…' : '発令を登録'}
              </button>
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
              効果日が本日の場合は即時反映されます。未来日の場合は効果日到来時に自動で乗下船処理されます。
            </div>
          </div>

          {/* 予約一覧 */}
          <div className="card" style={{ padding: 0, overflow: 'auto' }}>
            {ordersLoading ? (
              <div className="empty" style={{ padding: 40 }}>読み込み中…</div>
            ) : orders.length === 0 ? (
              <div className="empty" style={{ padding: 40 }}>予約されている発令はありません</div>
            ) : (
              <table>
                <thead><tr>
                  <th>効果日</th><th>区分</th><th>乗組員</th><th>船舶</th><th>役職</th><th>残り</th><th>操作</th>
                </tr></thead>
                <tbody>
                  {orders.map(o => {
                    const k = ORDER_KIND[o.order_type];
                    const left = daysUntil(o.effective_date);
                    return (
                      <tr key={`${o.order_type}-${o.assignment_id}`}>
                        <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtDate(o.effective_date)}</td>
                        <td><Pill bg={k.bg} fg={k.fg} border={k.border}>{k.label}</Pill></td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer' }} onClick={() => setDetailUserId(o.user_id)}>
                            <AvatarEditor entityId={o.user_id} type="user" imagePath={o.avatar_path} displayName={o.user_name} size={30} readOnly onUpdated={() => {}} />
                            <span style={{ fontWeight: 700 }}>{o.user_name}</span>
                          </div>
                        </td>
                        <td style={{ fontSize: 12.5 }}>{o.vessel_name ?? '-'}</td>
                        <td style={{ fontSize: 12.5 }}>{o.position ?? '-'}</td>
                        <td style={{ fontSize: 12.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                          {left != null && left >= 0 ? (left === 0 ? '本日' : `あと${left}日`) : '-'}
                        </td>
                        <td>
                          <button onClick={() => cancelOrder(o)}
                            style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', borderRadius: 6, cursor: 'pointer' }}>
                            取消
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ═══ 発令ダイアログ ═══ */}
      {issueOpen && (
        <div className="modal-overlay" onClick={() => setIssueOpen(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, width: '95%', maxHeight: '88vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2 style={{ fontSize: 17, fontWeight: 800, margin: 0 }}>配乗案を発令する</h2>
              <button onClick={() => setIssueOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
            </div>

            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>効果日（この日に自動反映）</div>
                <input type="date" value={effectiveDate} min={todayYmd()} onChange={e => setEffectiveDate(e.target.value)}
                  style={{ padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontWeight: 700 }} />
              </div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#374151', cursor: 'pointer', paddingBottom: 6 }}>
                <input type="checkbox" checked={notify} onChange={e => setNotify(e.target.checked)} style={{ width: 15, height: 15, accentColor: '#0066CC' }} />
                対象者へお知らせ配信する
              </label>
            </div>

            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>発令内容（{diffs.length}件）</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 16 }}>
              {diffs.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 12.5 }}>
                  <span style={{ fontWeight: 700, minWidth: 90 }}>{d.row.user_name}</span>
                  {d.kind === 'disembark' && <Pill bg="#fef2f2" fg="#dc2626" border="#fecaca">下船</Pill>}
                  {d.kind === 'board' && <Pill bg="#f0fdf4" fg="#16a34a" border="#bbf7d0">乗船</Pill>}
                  {d.kind === 'move' && <Pill bg="#fffbeb" fg="#b45309" border="#fde68a">移動</Pill>}
                  {d.kind === 'position' && <Pill bg="#eff6ff" fg="#2563eb" border="#bfdbfe">役職変更</Pill>}
                  <span style={{ color: '#64748b' }}>
                    {d.row.current?.vessel_name ?? '陸上'} → {d.toName}
                    {d.position && d.to !== null && <span>（{d.position}）</span>}
                  </span>
                  {d.to !== null && !(d.position ?? '').trim() && (
                    <Pill bg="#fef2f2" fg="#b91c1c" border="#fca5a5">役職未登録</Pill>
                  )}
                </div>
              ))}
            </div>
            {missingPosition.length > 0 && (
              <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#b91c1c', fontWeight: 700, marginBottom: 16 }}>
                役職が未登録の乗組員が {missingPosition.length} 名います（{missingPosition.map(d => d.row.user_name).join('、')}）。
                船に配置する乗組員は役職の登録が必須です。役職が無いと船長の書類作成などの権限が効きません。カードの役職を選んでから発令してください。
              </div>
            )}

            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '9px 12px', fontSize: 11.5, color: '#0369a1', marginBottom: 16 }}>
              効果日が本日の場合は即時反映されます。未来日の場合は「発令予約」に登録され、効果日到来時に自動で乗下船・配置換えが行われます。
              別船に乗船中の乗組員は効果日に自動で配置換え（旧船下船→新船乗船）されます。
            </div>

            <div className="flex-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-o" onClick={() => setIssueOpen(false)}>キャンセル</button>
              <button className="btn-p" disabled={issuing || !effectiveDate || missingPosition.length > 0} onClick={issue}
                title={missingPosition.length > 0 ? '役職が未登録の乗組員がいるため発令できません' : ''}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Send size={14} />{issuing ? '発令中…' : `発令する（${diffs.length}件）`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 個人詳細ドロワー */}
      {detailUserId && (
        <TalentDetailDrawer userId={detailUserId} onClose={() => setDetailUserId(null)}
          onChanged={() => loadRoster().then(r => { if (!dirty) setAssign(buildAssignFromCurrent(r)); })} />
      )}
    </div>
  );
};

export default CrewSimPage;
